/**
 * Leads Selection disposition pickers for Predictive / Manual / AI Agent:
 *  1. List Disposition codes — smart multi-select loaded from assigned lists (excl. DNC)
 *  2. System Disposition codes — catalog checkboxes (excl. DNC)
 * Both feed dial_statuses used for dialing.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CaretUpDown, Check, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  mergeDialStatuses,
  SYSTEM_DIAL_CATALOG_CODES,
} from "@/lib/campaignForm";

function Field({ label, required, hint, children, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Smart multi-select for List Disposition codes (loads on open / when lists change). */
function ListDispositionMultiSelect({
  listIds = [],
  selectedCodes = [],
  onChange,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [loadedKey, setLoadedKey] = useState("");

  const key = useMemo(
    () => (Array.isArray(listIds) ? [...listIds].filter(Boolean).sort().join(",") : ""),
    [listIds],
  );
  const selected = useMemo(
    () => (Array.isArray(selectedCodes) ? selectedCodes : []),
    [selectedCodes],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const load = useCallback(async (force = false) => {
    if (!key) {
      setItems([]);
      setLoadedKey("");
      return;
    }
    if (!force && loadedKey === key) return;
    setLoading(true);
    try {
      const { data } = await api.get("/customer-leads/list-disposition-codes", {
        params: { list_ids: key },
      });
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(rows.filter((r) => r.code && r.code !== "DNC"));
      setLoadedKey(key);
    } catch {
      setItems([]);
      setLoadedKey(key);
    } finally {
      setLoading(false);
    }
  }, [key, loadedKey]);

  // Prefetch when lists change
  useEffect(() => {
    if (!key) {
      setItems([]);
      setLoadedKey("");
      return;
    }
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch on list key change
  }, [key]);

  // Drop selections that are no longer present on the loaded lists
  useEffect(() => {
    if (!key || !loadedKey || loadedKey !== key || loading) return;
    const allowed = new Set(items.map((r) => r.code));
    const next = selected.filter((c) => allowed.has(c));
    if (next.length !== selected.length) onChange?.(next);
  }, [key, loadedKey, loading, items, selected, onChange]);

  const display = useMemo(() => {
    if (!key) return "Select list(s) first…";
    if (!selected.length) return "Select list disposition code(s)…";
    const names = selected.map((code) => {
      const row = items.find((i) => i.code === code);
      return row?.label || code;
    });
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [key, selected, items]);

  const toggle = (code) => {
    if (selectedSet.has(code)) onChange(selected.filter((x) => x !== code));
    else onChange([...selected, code]);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) load(false);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={!key}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3 bg-white"
        >
          <span className="truncate">{display}</span>
          {loading ? (
            <CircleNotch className="ml-2 h-3.5 w-3.5 shrink-0 animate-spin opacity-70" />
          ) : (
            <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search disposition codes…"
            data-testid={`${testid}-search`}
          />
          <CommandList>
            {loading && (
              <div
                className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground"
                data-testid={`${testid}-loading`}
              >
                <CircleNotch className="h-4 w-4 animate-spin" />
                Loading disposition codes from lists…
              </div>
            )}
            {!loading && (
              <>
                <CommandEmpty>
                  {key
                    ? "No disposition codes found on the selected lists."
                    : "Select one or more lists first."}
                </CommandEmpty>
                <CommandGroup>
                  {items.map((it) => {
                    const on = selectedSet.has(it.code);
                    return (
                      <CommandItem
                        key={it.code}
                        value={`${it.label} ${it.code}`}
                        data-testid={`${testid}-option-${it.code}`}
                        onSelect={() => toggle(it.code)}
                      >
                        <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${on ? "opacity-100" : "opacity-0"}`} />
                        <span className="truncate min-w-0 flex-1">{it.label}</span>
                        <span className="text-[10px] uppercase text-muted-foreground shrink-0 ml-2">
                          {it.code}
                        </span>
                        {it.lead_count != null && (
                          <span className="text-[10px] text-muted-foreground shrink-0 ml-2 tabular-nums">
                            {it.lead_count}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {object} value — form slice with listIds, listDialStatuses, systemDialStatuses
 * @param {(patch: object) => void} onChange
 * @param {string} testidPrefix
 */
export default function CampaignDispositionFields({
  value = {},
  onChange,
  testidPrefix = "campaign-dispos",
}) {
  const v = value || {};
  const set = (patch) => {
    const next = { ...v, ...patch };
    const dialStatuses = mergeDialStatuses(next.listDialStatuses, next.systemDialStatuses);
    onChange?.({ ...patch, dialStatuses });
  };

  const [systemRows, setSystemRows] = useState([]);
  const [systemLoading, setSystemLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSystemLoading(true);
      try {
        const { data } = await api.get("/customer-settings/dispositions");
        if (cancelled) return;
        const system = Array.isArray(data?.system) ? data.system : [];
        const custom = Array.isArray(data?.custom) ? data.custom : [];
        const seen = new Set();
        const rows = [];
        for (const row of [...system, ...custom]) {
          const code = String(row.code || "").toUpperCase();
          if (!code || code === "DNC" || seen.has(code)) continue;
          seen.add(code);
          rows.push({
            code,
            label: row.label || code,
            source: row.source || "system",
          });
        }
        setSystemRows(rows);
      } catch {
        if (!cancelled) {
          setSystemRows(
            SYSTEM_DIAL_CATALOG_CODES.map((code) => ({
              code,
              label: code,
              source: "system",
            })),
          );
        }
      } finally {
        if (!cancelled) setSystemLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const listSelected = Array.isArray(v.listDialStatuses) ? v.listDialStatuses : [];
  const systemSelected = Array.isArray(v.systemDialStatuses) ? v.systemDialStatuses : [];

  const toggleSystem = (code, checked) => {
    const cur = systemSelected;
    set({
      systemDialStatuses: checked
        ? (cur.includes(code) ? cur : [...cur, code])
        : cur.filter((x) => x !== code),
    });
  };

  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      <Field
        label="List Disposition codes"
        hint="Statuses found on leads in the selected lists (DNC never shown). Used together with System Disposition codes for dialing."
      >
        <ListDispositionMultiSelect
          listIds={v.listIds || []}
          selectedCodes={listSelected}
          onChange={(codes) => set({ listDialStatuses: codes })}
          testid={`${testidPrefix}-list`}
        />
      </Field>

      <Field
        label="System Disposition codes"
        hint="System and custom disposition codes. DNC is never shown. Used together with List Disposition codes for dialing."
      >
        <div
          className="rounded-md border bg-white p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto"
          data-testid={`${testidPrefix}-system`}
        >
          {systemLoading && (
            <p className="text-xs text-muted-foreground sm:col-span-2 flex items-center gap-2">
              <CircleNotch className="h-3.5 w-3.5 animate-spin" /> Loading…
            </p>
          )}
          {!systemLoading && systemRows.length === 0 && (
            <p className="text-xs text-muted-foreground sm:col-span-2">
              No disposition codes available.
            </p>
          )}
          {systemRows.map((d) => {
            const checked = systemSelected.includes(d.code);
            return (
              <label
                key={d.code}
                className="flex items-center gap-2 text-sm cursor-pointer"
                data-testid={`${testidPrefix}-system-${d.code}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(x) => toggleSystem(d.code, !!x)}
                />
                <span>{d.label}</span>
                <span className="text-[10px] uppercase text-muted-foreground">{d.code}</span>
              </label>
            );
          })}
        </div>
      </Field>
    </div>
  );
}
